"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import styles from "./css/service-selection-modal.module.css";
import { listServices } from "../../../../api/clinic/servicesApi";

// Mock services data for frontend-only search (will be replaced with API call)
const MOCK_SERVICES = [
  { servicecode: "BP", servicename: "Blood Pressure Test" },
  { servicecode: "GLUC", servicename: "Glucose Test" },
  { servicecode: "CHOLES", servicename: "Total Cholesterol Test" },
  { servicecode: "URINE", servicename: "Urine Dipstick Test" },
  { servicecode: "IRON", servicename: "Haemoglobin (Iron) Test" },
  { servicecode: "EAR", servicename: "Ear Syringing (Per Ear)" },
  { servicecode: "BABY ADMIN", servicename: "Baby Immunisation Administration" },
  { servicecode: "GEN CONSULT", servicename: "General Consult" },
  { servicecode: "CONSULT", servicename: "Diet Consult - Weekly" },
  { servicecode: "DIET PLUS", servicename: "Diet Plus Injection" },
  { servicecode: "PAP SMEAR", servicename: "Pap Smear (Cash Only)" },
  { servicecode: "FAMILY PLANNING", servicename: "Family Planning" },
  { servicecode: "WOUND CARE 1", servicename: "Wound Care (< 10 minutes)" },
  { servicecode: "WOUND CARE 2", servicename: "Wound Care (> 10 minutes)" },
  { servicecode: "ADMIN", servicename: "Injection Administration (Excl. Meds)" },
  { servicecode: "ADMIN PEAK", servicename: "Injection Administration (Peak)" },
  { servicecode: "PEAK FLOW", servicename: "Peak Flow Measurement" },
  { servicecode: "VACC ADULT", servicename: "Vaccination Adult (18 years and older)" },
  { servicecode: "VACC MINOR", servicename: "Minor Vaccination" },
  { servicecode: "HIV", servicename: "HIV Test (Pre & Post Counselling Incl.)" },
  { servicecode: "HEART", servicename: "Heart Disease" },
  { servicecode: "STROKE", servicename: "Stroke" },
  { servicecode: "CLINIC CONSULT", servicename: "Clinic Consult (Standard)" },
  { servicecode: "CLINIC CONSULT EXT", servicename: "Clinic Consult (Extended)" },
  { servicecode: "PCDT", servicename: "PCDT Consult (Primary Care Drug Therapist Pharmacist)" },
  { servicecode: "NEBULISE", servicename: "Nebulisation (Excl. Meds & Mask)" },
  { servicecode: "DIABETES", servicename: "Diabetes Consult (30 Minutes)" },
  { servicecode: "HRA CASH", servicename: "Health Risk Assessment (Cash)" },
  { servicecode: "VITALITY CASH", servicename: "Discovery Vitality Assessment (Cash)" },
  { servicecode: "MOMENTUM HRA", servicename: "Multiply HRA (Momentum - Medical Aid)" },
  { servicecode: "VITALITY MA", servicename: "Discovery Vitality (Medical Aid)" },
  { servicecode: "MENOPAUSE", servicename: "Metabolic / Menopause Clinic Consult" },
  { servicecode: "LIPOGRAM", servicename: "Clinic Lipogram" },
  { servicecode: "WELLNESS", servicename: "Wellness Screening" },
];

const VirtualizedServiceDropdown = ({ items, onSelect }) => {
  const ITEM_HEIGHT = 44;
  const OVERSCAN = 6;
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const stopScrollPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const totalHeight = items.length * ITEM_HEIGHT;
  const viewportHeight = 200;

  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    items.length,
    Math.ceil((scrollTop + viewportHeight) / ITEM_HEIGHT) + OVERSCAN
  );

  const visibleItems = useMemo(() => items.slice(startIndex, endIndex), [items, startIndex, endIndex]);
  const offsetY = startIndex * ITEM_HEIGHT;

  return (
    <div
      className={styles.searchDropdown}
      onScroll={onScroll}
      onWheel={stopScrollPropagation}
      onTouchMove={stopScrollPropagation}
    >
      <div style={{ height: totalHeight, position: "relative" }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleItems.map((service) => (
            <div
              key={service?.serviceid ?? `${service?.servicecode ?? ""}::${service?.servicename ?? ""}`}
              className={styles.dropdownItem}
              onClick={() => onSelect(service)}
              style={{ height: ITEM_HEIGHT }}
            >
              <span className={styles.dropdownCode}>{service.servicecode}</span>
              <span className={styles.dropdownName}>{service.servicename}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

VirtualizedServiceDropdown.propTypes = {
  items: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const ServiceSelectionModal = ({ isOpen, onClose, onProceed, defaultServiceName = "" }) => {
  const [serviceCode, setServiceCode] = useState("");
  const [serviceName, setServiceName] = useState(defaultServiceName);
  const [selectedService, setSelectedService] = useState(null);
  const [services, setServices] = useState([]);
  const [codeSearchResults, setCodeSearchResults] = useState([]);
  const [nameSearchResults, setNameSearchResults] = useState([]);
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [showNameDropdown, setShowNameDropdown] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await listServices();
        if (!cancelled) setServices(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setServices([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const sourceServices = services.length > 0 ? services : MOCK_SERVICES;

  const handleCodeSearch = (value) => {
    setServiceCode(value);
    setSelectedService(null);
    if (value.trim() === "") {
      setCodeSearchResults([]);
      setShowCodeDropdown(false);
      return;
    }
    const results = sourceServices.filter((s) =>
      (s.servicecode || "").toLowerCase().includes(value.toLowerCase())
    );
    setCodeSearchResults(results);
    setShowCodeDropdown(results.length > 0);
  };

  const handleNameSearch = (value) => {
    setServiceName(value);
    setSelectedService(null);
    if (value.trim() === "") {
      setNameSearchResults([]);
      setShowNameDropdown(false);
      return;
    }
    const results = sourceServices.filter((s) =>
      (s.servicename || "").toLowerCase().includes(value.toLowerCase())
    );
    setNameSearchResults(results);
    setShowNameDropdown(results.length > 0);
  };

  const selectServiceByCode = (service) => {
    setServiceCode(service.servicecode);
    setServiceName(service.servicename);
    setSelectedService(service);
    setShowCodeDropdown(false);
    setShowNameDropdown(false);
  };

  const selectServiceByName = (service) => {
    setServiceCode(service.servicecode);
    setServiceName(service.servicename);
    setSelectedService(service);
    setShowCodeDropdown(false);
    setShowNameDropdown(false);
  };

  const handleClose = () => {
    setServiceCode("");
    setServiceName("");
    setSelectedService(null);
    setCodeSearchResults([]);
    setNameSearchResults([]);
    setShowCodeDropdown(false);
    setShowNameDropdown(false);
    onClose();
  };

  const handleProceed = () => {
    const resolvedService =
      selectedService ||
      sourceServices.find((s) => (s.servicecode || "") === serviceCode) ||
      sourceServices.find((s) => (s.servicename || "") === (serviceName || defaultServiceName));

    onProceed({
      serviceid: resolvedService?.serviceid,
      serviceCode,
      serviceName: serviceName || defaultServiceName,
    });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={handleClose} closeStyle="plain">
      <div className={styles.serviceModalContent}>
        <h2 className={styles.serviceModalTitle}>Select Service Type</h2>
        
        <div className={styles.serviceSearchRow}>
          {/* Look Up Code */}
          <div className={styles.searchFieldContainer}>
            <label className={styles.searchLabel}>Look Up Code</label>
            <div className={styles.searchInputWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                value={serviceCode}
                onChange={(e) => handleCodeSearch(e.target.value)}
                placeholder="Enter service code"
              />
              <span className={styles.searchIcon}>🔍</span>
            </div>
            {showCodeDropdown && (
              <VirtualizedServiceDropdown items={codeSearchResults} onSelect={selectServiceByCode} />
            )}
          </div>

          {/* Look Up Service */}
          <div className={styles.searchFieldContainer}>
            <label className={styles.searchLabel}>Look Up Service</label>
            <div className={styles.searchInputWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                value={serviceName}
                onChange={(e) => handleNameSearch(e.target.value)}
                placeholder="Enter service name"
              />
              <span className={styles.searchIcon}>🔍</span>
            </div>
            {showNameDropdown && (
              <VirtualizedServiceDropdown items={nameSearchResults} onSelect={selectServiceByName} />
            )}
          </div>
        </div>

        <div className={styles.serviceModalActions}>
          <Button
            variant="primary"
            onClick={handleProceed}
            disabled={!serviceCode && !serviceName}
          >
            Proceed to Consultation
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ServiceSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  defaultServiceName: PropTypes.string,
};

export default ServiceSelectionModal;
